import { criarTabelaPrecoSchema, novaVigenciaSchema, percentualParaTaxa, type NovaVigenciaDto, type TabelaPrecoResponse } from '@triade/contracts';
import { ApiError } from '@/lib/api';

export type DespesaTipo = NovaVigenciaDto['despesas'][number]['kind'];

export const TIPO_DESPESA_ROTULO: Record<DespesaTipo, string> = { TRIBUTO: 'Tributo', COMISSAO: 'Comissão', OUTRA: 'Outra' };

type DespesaCadastro = NovaVigenciaDto['despesas'][number];
/** "É tributo?": '' = nao e tributo (taxCode null). */
export type TaxCodeEntrada = '' | NonNullable<DespesaCadastro['taxCode']>;
export type BaseDespesa = DespesaCadastro['base'];

export const TAXCODE_ROTULO: Record<TaxCodeEntrada, string> = { '': '—', ICMS: 'ICMS', ISS: 'ISS', PIS: 'PIS', COFINS: 'COFINS', OUTRO: 'Outro (IRPJ, CSLL, DAS)' };
export const BASE_DESPESA_ROTULO: Record<BaseDespesa, string> = { PRECO_SEM_TRIBUTOS: 'Preço sem tributos', TOTAL_NF: 'Total da NF' };

/** Aviso (nao bloqueia): despesa do tipo TRIBUTO sem ICMS/ISS/PIS/COFINS marcado. */
export const AVISO_TRIBUTO_NAO_MARCADO =
  'Despesa de tributo sem marcar ICMS/ISS/PIS/COFINS/Outro: a partir de 2027 ela não recebe o fator de transição do ano.';

/** Linha da grade de despesas, como o usuario digita (percentual em pontos: "18" = 18%). */
export interface DespesaEntrada {
  tipo: DespesaTipo;
  descricao: string;
  percentual: string;
  taxCode: TaxCodeEntrada;
  base: BaseDespesa;
}

/** Formulario "Nova vigencia": percentuais em texto digitado; a conversao para fracao e so `percentualParaTaxa`. */
export interface NovaVigenciaEntrada {
  validFrom: string;
  lucroDesejado: string;
  lucroMinimo: string;
  motivo: string;
  despesas: DespesaEntrada[];
}

export const DESPESA_VAZIA: DespesaEntrada = { tipo: 'TRIBUTO', descricao: '', percentual: '', taxCode: '', base: 'PRECO_SEM_TRIBUTOS' };

/** Despesa de tributo sem ICMS/ISS/PIS/COFINS marcado (o aviso da tela; a API nao bloqueia). */
export const tributoNaoMarcado = (linha: Pick<DespesaEntrada, 'tipo' | 'taxCode' | 'descricao'>): boolean =>
  linha.tipo === 'TRIBUTO' && linha.taxCode === '' && linha.descricao.trim() !== '';

export const hojeIso = (): string => new Date().toLocaleDateString('en-CA');

export function novaVigenciaVazia(): NovaVigenciaEntrada {
  return { validFrom: hojeIso(), lucroDesejado: '', lucroMinimo: '', motivo: '', despesas: [] };
}

export type Resultado<T> = { ok: true; dados: T } | { ok: false; mensagem: string };

const vazio = (texto: string): boolean => texto.trim() === '';

/** Vazio = null (vale o padrao da empresa, DP6). Digitado: vira fracao via `percentualParaTaxa`. */
function taxaOpcional(texto: string, rotulo: string): Resultado<string | null> {
  if (vazio(texto)) return { ok: true, dados: null };
  const taxa = percentualParaTaxa(texto);
  if (taxa === null) return { ok: false, mensagem: `${rotulo}: informe um percentual entre 0 e 100` };
  return { ok: true, dados: taxa };
}

/** Monta o corpo de POST /tabelas-preco/:id/vigencias. Linhas de despesa totalmente vazias sao ignoradas. */
export function paraNovaVigencia(e: NovaVigenciaEntrada): Resultado<NovaVigenciaDto> {
  if (vazio(e.validFrom)) return { ok: false, mensagem: 'Informe o início da vigência' };
  const desejado = taxaOpcional(e.lucroDesejado, 'Lucro desejado');
  if (!desejado.ok) return desejado;
  const minimo = taxaOpcional(e.lucroMinimo, 'Lucro mínimo');
  if (!minimo.ok) return minimo;

  const despesas: NovaVigenciaDto['despesas'] = [];
  for (const linha of e.despesas) {
    if (vazio(linha.descricao) && vazio(linha.percentual)) continue;
    if (vazio(linha.descricao)) return { ok: false, mensagem: 'Despesa sem descrição' };
    const taxa = percentualParaTaxa(linha.percentual);
    if (taxa === null) return { ok: false, mensagem: `Despesa "${linha.descricao.trim()}": informe um percentual entre 0 e 100` };
    despesas.push({
      description: linha.descricao.trim(),
      kind: linha.tipo,
      rate: taxa,
      taxCode: linha.taxCode === '' ? null : linha.taxCode,
      base: linha.base,
    });
  }

  const bruto: NovaVigenciaDto = {
    validFrom: e.validFrom,
    desiredProfitRate: desejado.dados,
    minimumProfitRate: minimo.dados,
    despesas,
    ...(vazio(e.motivo) ? {} : { motivo: e.motivo.trim() }),
  };
  const valido = novaVigenciaSchema.safeParse(bruto);
  if (!valido.success) return { ok: false, mensagem: valido.error.issues[0]?.message ?? 'Confira os campos da vigência' };
  return { ok: true, dados: valido.data };
}

/** Valores do cadastro da tabela (identificador so na criacao). */
export interface TabelaEntrada {
  codigo: string;
  nome: string;
  isDefault: boolean;
  ativa: boolean;
  /** P3: o preco praticado digitado ja e o total da NF (venda a nao contribuinte). Padrao: desmarcado. */
  precoIncluiCbsIbs: boolean;
}

export const tabelaVazia = (): TabelaEntrada => ({ codigo: '', nome: '', isDefault: false, ativa: true, precoIncluiCbsIbs: false });

export const entradaDeTabela = (t: TabelaPrecoResponse): TabelaEntrada => ({
  codigo: t.codigo,
  nome: t.nome,
  isDefault: t.isDefault,
  ativa: t.ativa,
  precoIncluiCbsIbs: t.precoIncluiCbsIbs,
});

export function validarCriacao(e: TabelaEntrada): Resultado<{ codigo: string; nome: string; isDefault: boolean; ativa: boolean; precoIncluiCbsIbs: boolean }> {
  const r = criarTabelaPrecoSchema.safeParse(e);
  if (!r.success) return { ok: false, mensagem: r.error.issues[0]?.message ?? 'Confira identificador e nome' };
  return { ok: true, dados: r.data };
}

/** Texto do erro da API para o usuario. VIGENCIA_RETROATIVA ganha mensagem propria. */
export function mensagemErroTabela(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VIGENCIA_RETROATIVA') {
      return 'Início retroativo não é permitido: a nova vigência começa hoje ou depois. Nada muda no passado.';
    }
    return `${e.message}${e.code ? ` (${e.code})` : ''}`;
  }
  return 'falha de comunicação com a API';
}
